import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialStateLabel, materialStateEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailyRentalMutation, useDailyRentalMutation } from '../../../../hooks/mutations/useDailyRentalMutation';
import { useRentalsQuery } from '../../../../hooks/queries/useRentalsQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DailyRental } from '../../../../services/apiSLTP/dailyReports/dailyRentals';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import QuantityToggleGroup from '../../../ui/QuantityToggleGroup';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailyRentalModalProps extends ModalProps {
    refetch: () => void;
    dailyRental: DailyRental | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailyRentalModal: React.FC<ManageValidationDailyRentalModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyRental,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailyRentalId] = useState(dailyRental?._id || '');
    const [designation, setDesignation] = useState(dailyRental?.designation || '');
    const [selectedRentalType, setSelectedRentalType] = useState<OptionType | null>( dailyRental?.rentalTypeId ?{
        label: dailyRental?.rentalTypeId.designation,
        value: dailyRental?.rentalTypeId._id,
    } : null);
    const [selectedActivity, setSelectedActivity] = useState<OptionType | null>( dailyRental?.activityId ? {
        label: activities.find((activity) => activity.value === dailyRental?.activityId)?.label || '',
        value: dailyRental?.activityId || '',
    } : null);
    const [selectedState, setSelectedState] = useState<OptionType>({
        label: capitalizeFirstLetter(t(getMaterialStateLabel(dailyRental?.state ?? materialStateEnum.PRESENT))),
        value: dailyRental?.state.toString() || materialStateEnum.PRESENT.toString(),
    });
    const [unitPrice, setUnitPrice] = useState(dailyRental?.unitPrice?.toString() || '');
    const [quantity, setQuantity] = useState<number>(dailyRental?.quantity ?? 1);
    const [isExposedToWeather, setIsExposedToWeather] = useState(dailyRental?.isExposedToWeather ?? false);

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.NEW))), value: materialStateEnum.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.PRESENT))), value: materialStateEnum.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.REMOVE))), value: materialStateEnum.REMOVE.toString() },
    ];

    const { data: dataRentals } = useRentalsQuery({ queries: { active: true } });

    const { mutate: createDailyRental } = useCreateDailyRentalMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailyRental } = useDailyRentalMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailyRental = async () => {
        if (!selectedActivity || !selectedRentalType) return;
        createDailyRental({
            body: {
                activityId: selectedActivity.value,
                rentalTypeId: selectedRentalType.value,
                state: parseInt(selectedState.value),
                designation,
                unitPrice: unitPrice.length ? parseFloat(unitPrice) : undefined,
                quantity,
                isExposedToWeather,
            },
        });
    };

    const patchDailyRental = async () => {
        if (currentDailyRentalId)
            updateDailyRental({
                id: currentDailyRentalId,
                body: {
                    activityId: selectedActivity?.value,
                    rentalTypeId: selectedRentalType?.value,
                    state: parseInt(selectedState.value),
                    designation,
                    unitPrice: unitPrice.length ? parseFloat(unitPrice) : null,
                    quantity,
                    isExposedToWeather,
                },
            });
    };

    return (
        <ModalBody
            title={t('rental')}
            subtitle={!!currentDailyRentalId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentDailyRentalId.length ? postDailyRental() : patchDailyRental();
            }}
            disableSubmit={!selectedRentalType?.value.length || !selectedActivity?.value.length || !selectedState?.value.length || !designation.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity(value)}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedRentalType}
                items={
                    dataRentals?.map((rental) => ({
                        label: rental.designation,
                        value: rental._id,
                    })) || []
                }
                handleChange={(event, value) => setSelectedRentalType(value)}
                inputLabel={t('company')}
                required
            />
            <CustomTextInput value={designation} onChange={(value) => setDesignation(value)} label={t('name')} required />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => value ? setSelectedState(value) : null}
                inputLabel={t('state')}
                required
            />
            <ToggleLine
                checked={isExposedToWeather}
                onChange={(checked) => setIsExposedToWeather(checked)}
                title={capitalizeFirstLetter(t('badWeather'))}
            />
            <QuantityToggleGroup
                value={quantity}
                onChange={(value) => setQuantity(value)}
                options={[
                    { label: '0j', value: 0 },
                    { label: '0.5j', value: 0.5, disabled: true },
                    { label: '1j', value: 1 },
                ]}
            />

            <CustomTextInput
                value={unitPrice}
                onChange={setUnitPrice}
                label={t('unitPrice')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
        </ModalBody>
    );
};

export default ManageValidationDailyRentalModal;
